import { useState } from 'react';
import { useApp } from '../../context/AppContext';

import { predefinedIcons } from '../../constants/menuIcons';

function Step5() {
  const { state, dispatch } = useApp();
  const { appData } = state;
  const [hasMenu, setHasMenu] = useState(appData.hasMenu || false);
  const [menuColor, setMenuColor] = useState(appData.menuColor || '#3B82F6');
  const [selectedIcons, setSelectedIcons] = useState(appData.icons || []);

  const toggleIconSelection = (icon) => {
    if (selectedIcons.find(i => i.id === icon.id)) {
      setSelectedIcons(selectedIcons.filter(i => i.id !== icon.id));
    } else if (selectedIcons.length < 5) {
      setSelectedIcons([...selectedIcons, icon]);
    }
  };

  // Função para verificar se o botão próximo deve estar habilitado
  const isNextButtonEnabled = () => {
    // Se não tem menu, pode avançar sempre
    if (!hasMenu) return true;
    
    // Se tem menu, só pode avançar se tiver pelo menos 2 ícones selecionados
    return selectedIcons.length >= 2;
  };

  const handleNext = () => {
    // Verificar novamente antes de prosseguir (segurança extra)
    if (hasMenu && selectedIcons.length < 2) {
      alert('Por favor, selecione pelo menos 2 ícones para continuar.');
      return;
    }

    dispatch({ 
      type: 'UPDATE_APP_DATA', 
      payload: { 
        hasMenu, 
        menuColor, 
        icons: selectedIcons 
      } 
    });
    
    // Calcular valor do menu e ícones
    let menuCost = 0;
    let iconsCost = 0;
    
    if (hasMenu) {
      menuCost = state.pricingConfig?.menu || 0; // Custo base do menu
      iconsCost = selectedIcons.length * (state.pricingConfig?.icons || 0); // Custo por ícone
    }
    
    dispatch({ type: 'UPDATE_PRICE', payload: { menu: menuCost, icons: iconsCost } });
    dispatch({ type: 'NEXT_STEP' });
  };

  const handleBack = () => {
    dispatch({ type: 'PREV_STEP' });
  };

  // Resetar ícones selecionados quando desmarcar o menu
  const handleMenuToggle = (checked) => {
    setHasMenu(checked);
    if (!checked) {
      setSelectedIcons([]);
    }
  };

  return (
    <div className="bg-white p-8 rounded-2xl shadow-xl">
      <h2 className="text-3xl font-bold mb-8 text-gray-900">Menu e Ícones</h2>
      
      <div className="space-y-8">
        <div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={hasMenu}
              onChange={(e) => handleMenuToggle(e.target.checked)}
              className="h-5 w-5 text-gray-900 focus:ring-gray-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-lg font-medium text-gray-900">
              Adicionar menu de navegação
            </span>
          </label>
        </div>

        {hasMenu && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Cor do menu
              </label>
              <div className="flex items-center">
                <input
                  type="color"
                  value={menuColor}
                  onChange={(e) => setMenuColor(e.target.value)}
                  className="h-12 w-12 rounded-xl border border-gray-200 cursor-pointer shadow-sm"
                />
                <span className="ml-3 text-lg font-medium text-gray-900">{menuColor}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Selecione os ícones do menu (mínimo 2, máximo 5)
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {predefinedIcons.map(icon => {
                  const IconComponent = icon.icon;
                  const isSelected = selectedIcons.find(i => i.id === icon.id);
                  return (
                    <div
                      key={icon.id}
                      className={`relative rounded-2xl p-3 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ${
                        isSelected
                          ? 'bg-blue-50 border-2 border-blue-500 shadow-md scale-105'
                          : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      } ${selectedIcons.length >= 5 && !isSelected ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                      onClick={() => toggleIconSelection(icon)}
                    >
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full p-1 shadow-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      <IconComponent className={`w-7 h-7 mb-2 transition-colors ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                      <p className={`text-[11px] font-semibold text-center truncate w-full ${isSelected ? 'text-blue-800' : 'text-gray-600'}`}>
                        {icon.name}
                      </p>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-sm text-gray-600">
                {selectedIcons.length} de 5 ícones selecionados
                {selectedIcons.length < 2 && (
                  <span className="text-red-600 font-medium ml-2">
                    (Selecione pelo menos {2 - selectedIcons.length} mais para habilitar o botão "Próximo")
                  </span>
                )}
              </p>
            </div>

            <div className="bg-gray-50 border-l-4 border-gray-900 p-6 rounded-r-xl">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-base text-gray-900">
                    <strong>Valor:</strong> {state.pricingConfig?.menu ? `R$ ${(state.pricingConfig.menu).toFixed(2).replace('.', ',')} (menu)` : 'Menu Grátis'} + {state.pricingConfig?.icons ? `R$ ${(state.pricingConfig.icons).toFixed(2).replace('.', ',')} por ícone` : 'Ícones Grátis'}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-10 flex justify-between">
        <button
          onClick={handleBack}
          className="bg-gray-100 text-gray-900 px-8 py-3 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium transition-all duration-200"
        >
          Voltar
        </button>
        <button
          onClick={handleNext}
          disabled={!isNextButtonEnabled()}
          className={`px-8 py-3 rounded-xl focus:outline-none focus:ring-2 font-medium shadow-lg transition-all duration-200 ${
            isNextButtonEnabled()
              ? 'bg-gray-900 text-white hover:bg-black hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-gray-500'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
          }`}
        >
          Próximo
        </button>
      </div>
    </div>
  );
}

export default Step5;